import { motion } from 'motion/react';
import { X, Trash2, FileEdit, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { useUserData } from './UserDataContext';

interface ShoutedYapOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasCaption?: boolean;
  onDeleteCaption: () => void;
  onDeleteShout: () => void;
  onShare?: () => void;
  yap: any;
}

export function ShoutedYapOptionsModal({
  isOpen,
  onClose,
  onDeleteCaption,
  onDeleteShout,
  onShare,
  yap,
}: ShoutedYapOptionsModalProps) {
  const { shoutedYaps } = useUserData();

  // Find the shouted yap to check if it has a caption
  const shoutedYap = shoutedYaps.find(shout => shout.originalYap.id === yap.id);
  const hasCaption = shoutedYap?.shoutCaption && shoutedYap.shoutCaption.trim().length > 0;

  const handleDeleteCaption = () => {
    onDeleteCaption();
    onClose();
  };

  const handleDeleteShout = () => {
    onDeleteShout();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm p-0 bg-[#e7d299] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 overflow-hidden">
        <DialogTitle className="sr-only">Manage Shouted Yap</DialogTitle>
        <DialogDescription className="sr-only">
          Choose to delete the caption or remove the entire shout
        </DialogDescription>

        {/* Header */}
        <div className="p-4 border-b border-black/10 dark:border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg text-black dark:text-white">Manage Shout</h2>
            <button
              onClick={onClose}
              className="text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Options */}
        <div className="p-4 space-y-3">
          {hasCaption && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDeleteCaption}
              className="w-full p-4 rounded-xl bg-[#F5F5F5] dark:bg-[#2a2a2a] border-2 border-black/10 dark:border-white/10 hover:border-[#F39C12] transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#F39C12]/20 dark:bg-[#4478ea]/20 flex items-center justify-center">
                  <FileEdit className="w-6 h-6 text-[#F39C12] dark:text-[#4478ea]" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-black dark:text-white">Delete Caption</p>
                  <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">
                    Keep the shout, remove your caption
                  </p>
                </div>
              </div>
            </motion.button>
          )}

          {onShare && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onShare}
              className="w-full p-4 rounded-xl bg-[#F5F5F5] dark:bg-[#2a2a2a] border-2 border-black/10 dark:border-white/10 hover:border-[#3498db] transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#3498db]/20 flex items-center justify-center">
                  <Share2 className="w-6 h-6 text-[#3498db]" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-black dark:text-white">Share Shout</p>
                  <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">
                    Share this shout with others
                  </p>
                </div>
              </div>
            </motion.button>
          )}

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleDeleteShout}
            className="w-full p-4 rounded-xl bg-[#F5F5F5] dark:bg-[#2a2a2a] border-2 border-black/10 dark:border-white/10 hover:border-[#E74C3C] transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#E74C3C]/20 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-[#E74C3C]" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-black dark:text-white">Delete Shout</p>
                <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">
                  Remove this shout completely
                </p>
              </div>
            </div>
          </motion.button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
